/** Segment in a feed post category path (root → leaf). */
export interface CategoryPathSegment {
  slug: string;
  name?: string;
  [key: string]: any;
}

export interface SubcategorySection<T> {
  key: string;
  title: string | null;
  items: T[];
}

type ResolveResult =
  | { kind: 'parent' }
  | { kind: 'sub'; slug: string; name?: string }
  | { kind: 'none' };

/**
 * Under a parent category filter, decide whether an item sits on the parent
 * itself or in an immediate subcategory (via category_paths).
 */
export function resolveSubcategoryPlacement(
  categoryPaths: CategoryPathSegment[][] | undefined | null,
  filterSlugs: string[],
): ResolveResult {
  if (!categoryPaths?.length || !filterSlugs.length) {
    return { kind: 'none' };
  }

  const hasExactParentMatch = categoryPaths.some(
    (path) => path.length === 1 && filterSlugs.includes(path[0].slug),
  );
  if (hasExactParentMatch) {
    return { kind: 'parent' };
  }

  let mostSpecificPath: CategoryPathSegment[] | null = null;
  let categoryIndex = -1;

  for (const path of categoryPaths) {
    const idx = path.findIndex((cat) => filterSlugs.includes(cat.slug));
    if (idx !== -1) {
      if (!mostSpecificPath || path.length > mostSpecificPath.length) {
        mostSpecificPath = path;
        categoryIndex = idx;
      }
    }
  }

  if (!mostSpecificPath || categoryIndex === -1) {
    return { kind: 'none' };
  }

  const subcategory = mostSpecificPath[categoryIndex + 1];
  if (subcategory) {
    return { kind: 'sub', slug: subcategory.slug, name: subcategory.name };
  }

  // Path matched parent but has no child segment — treat as parent-direct
  return { kind: 'parent' };
}

/**
 * Group items into parent + subcategory sections (PhotoGrid-style).
 * Returns a single untitled section when filterSlugs is empty.
 */
export function groupBySubcategory<T>(
  items: T[],
  getCategoryPaths: (item: T) => CategoryPathSegment[][] | undefined | null,
  filterSlugs: string[],
): SubcategorySection<T>[] {
  if (!filterSlugs.length) {
    return [{ key: 'all', title: null, items }];
  }

  const grouped = new Map<string, { title: string; items: T[] }>();
  const parentCategoryItems: T[] = [];

  for (const item of items) {
    const placement = resolveSubcategoryPlacement(getCategoryPaths(item), filterSlugs);
    if (placement.kind === 'parent') {
      parentCategoryItems.push(item);
    } else if (placement.kind === 'sub') {
      const existing = grouped.get(placement.slug);
      if (existing) {
        existing.items.push(item);
      } else {
        grouped.set(placement.slug, {
          title: (placement.name || placement.slug).replace(/-/g, ' '),
          items: [item],
        });
      }
    }
  }

  const sections: SubcategorySection<T>[] = [];

  if (parentCategoryItems.length > 0) {
    sections.push({
      key: filterSlugs[0],
      title: filterSlugs[0].replace(/-/g, ' '),
      items: parentCategoryItems,
    });
  }

  for (const [key, { title, items: sectionItems }] of grouped.entries()) {
    sections.push({ key, title, items: sectionItems });
  }

  if (sections.length === 0) {
    return [{ key: 'all', title: null, items }];
  }

  return sections;
}

/**
 * Filter feed items to one subcategory (or all under the parent when activeSubSlug is null).
 */
export function filterBySubcategory<T>(
  items: T[],
  getCategoryPaths: (item: T) => CategoryPathSegment[][] | undefined | null,
  filterSlugs: string[],
  activeSubSlug: string | null,
): T[] {
  if (!activeSubSlug || !filterSlugs.length) {
    return items;
  }

  return items.filter((item) => {
    const placement = resolveSubcategoryPlacement(getCategoryPaths(item), filterSlugs);
    return placement.kind === 'sub' && placement.slug === activeSubSlug;
  });
}
